import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TagInput } from '@/shared/ui/components/form/TagInput';
import { Text } from '@/shared/ui';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { PropertyCodeField } from './PropertyCodeField';
import { SuggestionPicker } from './SuggestionPicker';
import { useAgencyRequired } from './agency-required-context';
import type { PropertyFormValues } from './schema';
import { PROPERTY_FIELD_LIMITS } from './schema';
import { getListingSuggestions } from '@/services/housien/listingSuggestions';

interface PublicationContentSectionProps {
  form: UseFormReturn<PropertyFormValues>;
  agencyId: string;
}

/**
 * Renders publication fields: the agency's property code, title (with
 * suggestion picker), description (with suggestion picker), and differentials
 * tag input.
 */
export function PublicationContentSection({ form, agencyId }: PublicationContentSectionProps) {
  const { t } = useTranslation('features/properties');
  const errors = form.formState.errors;
  const descriptionRequired = useAgencyRequired('description');

  return (
    <div className="flex flex-col gap-(--space-4)">
      <PropertyCodeField form={form} />

      <Controller
        name="property_title"
        control={form.control}
        render={({ field }) => (
          <div className="flex flex-col gap-(--space-2)">
            <div className="flex items-end gap-(--space-2)">
              <div className="flex-1">
                <TextInput
                  label={t('propertyDrawer.publication.titleLabel')}
                  required
                  value={field.value}
                  error={errors.property_title?.message}
                  placeholder={t('propertyDrawer.publication.titlePlaceholder')}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  maxLength={PROPERTY_FIELD_LIMITS.propertyTitle.maxLength}
                  data-field="property_title"
                />
              </div>
              <SuggestionPicker
                kind="title"
                ariaLabel={t('propertyDrawer.suggestions.suggestTitle')}
                fetchSuggestions={() => getListingSuggestions(agencyId, 'title', form.getValues())}
                onSelect={s => form.setValue('property_title', s, { shouldDirty: true, shouldValidate: true })}
              />
            </div>
          </div>
        )}
      />

      <Controller
        name="description"
        control={form.control}
        render={({ field }) => (
          <div className="flex flex-col gap-(--space-2)">
            <div className="flex items-start gap-(--space-2)">
              <div className="flex-1">
                <TextInput
                  asTextArea
                  label={t('propertyDrawer.publication.descriptionLabel')}
                  required={descriptionRequired}
                  value={field.value ?? ''}
                  error={errors.description?.message}
                  placeholder={t('propertyDrawer.publication.descriptionPlaceholder')}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  maxLength={PROPERTY_FIELD_LIMITS.description.maxLength}
                  data-field="description"
                />
              </div>
              <SuggestionPicker
                kind="description"
                ariaLabel={t('propertyDrawer.suggestions.suggestDescription')}
                fetchSuggestions={() => getListingSuggestions(agencyId, 'description', form.getValues())}
                onSelect={s => form.setValue('description', s, { shouldDirty: true, shouldValidate: true })}
              />
            </div>
          </div>
        )}
      />

      <Controller
        name="tags"
        control={form.control}
        render={({ field }) => (
          <div>
            <Text as="span" className="block mb-(--space-1-5) text-(--text-secondary)" style={{ fontSize: 'var(--text-small)', fontWeight: 600, fontFamily: 'var(--font-family)' }}>
              {t('propertyDrawer.publication.differentials')}
            </Text>
            <TagInput
              value={field.value || []}
              onChange={field.onChange}
              placeholder={t('propertyDrawer.publication.tagsPlaceholder')}
            />
          </div>
        )}
      />

      <Controller
        name="highlights"
        control={form.control}
        render={({ field }) => (
          <div>
            <Text as="span" className="block mb-(--space-1-5) text-(--text-secondary)" style={{ fontSize: 'var(--text-small)', fontWeight: 600, fontFamily: 'var(--font-family)' }}>
              {t('propertyDrawer.publication.highlights')}
            </Text>
            <TagInput
              value={field.value || []}
              onChange={field.onChange}
              placeholder={t('propertyDrawer.publication.highlightsPlaceholder')}
            />
          </div>
        )}
      />
    </div>
  );
}
